import { Fragment, ReactElement, ReactNode, useId, useState } from 'react'
import { formatCents } from '../format'
import * as S from './styles'

export interface CommissionerMatrixCell {
  quantity: number
  revenueCents: number
}

export interface CommissionerMatrixTotal extends CommissionerMatrixCell {
  feeCents?: number
}

export interface CommissionerMatrixDetail extends CommissionerMatrixCell {
  key: string
  label: string
}

export interface CommissionerMatrixRow {
  key: string
  label: string
  cells: Array<CommissionerMatrixCell | null>
  total: CommissionerMatrixTotal
  details: CommissionerMatrixDetail[]
}

export interface CommissionerMatrixData {
  columns: Array<{ key: string, label: string }>
  rows: CommissionerMatrixRow[]
  total: { cells: Array<CommissionerMatrixCell | null>, total: CommissionerMatrixTotal }
  showFee: boolean
}

interface CommissionerMatrixProps {
  data: CommissionerMatrixData
  mobile?: boolean
  expandAll?: boolean
  renderDetail?: (rowKey: string) => ReactNode
}

const count = (value: number): string => value.toLocaleString('pt-BR')

function Amount({ cell }: { cell: CommissionerMatrixCell | null }): ReactElement {
  if (cell === null) return <S.Empty aria-label="Sem vendas">—</S.Empty>
  return (
    <>
      <S.Quantity>{count(cell.quantity)}</S.Quantity>
      <S.Money>{formatCents(cell.revenueCents)}</S.Money>
    </>
  )
}

const detailsText = (details: CommissionerMatrixDetail[]): string =>
  details.map((item) => `${item.label}: ${count(item.quantity)} · ${formatCents(item.revenueCents)}`).join('  |  ')

function DesktopMatrix({ data, expandAll = false, renderDetail }: CommissionerMatrixProps): ReactElement {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const baseId = useId()
  const width = data.columns.length + (data.showFee ? 3 : 2)
  const toggle = (key: string): void => {
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <S.Scroll>
      <S.Table>
        <thead>
          <tr>
            <S.HeadCell scope="col" $sticky>Comissário</S.HeadCell>
            {data.columns.map((column) => (
              <S.HeadCell key={column.key} scope="col" $numeric>{column.label}</S.HeadCell>
            ))}
            <S.HeadCell scope="col" $numeric>Total</S.HeadCell>
            {data.showFee && <S.HeadCell scope="col" $numeric>Taxa</S.HeadCell>}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => {
            const open = expandAll || expanded.has(row.key)
            const detailId = `${baseId}-${row.key}`
            return (
              <Fragment key={row.key}>
                <S.Row $open={open}>
                  <S.RowHead scope="row" $sticky>
                    <S.Toggle type="button" aria-expanded={open} aria-controls={detailId} onClick={() => toggle(row.key)}>
                      <S.Chevron aria-hidden="true" $open={open}>›</S.Chevron>
                      <span>{row.label}</span>
                    </S.Toggle>
                  </S.RowHead>
                  {row.cells.map((cell, index) => (
                    <S.Cell key={data.columns[index].key} $numeric>
                      <Amount cell={cell} />
                    </S.Cell>
                  ))}
                  <S.Cell $numeric $strong>
                    <Amount cell={row.total} />
                  </S.Cell>
                  {data.showFee && <S.Cell $numeric>{formatCents(row.total.feeCents ?? 0)}</S.Cell>}
                </S.Row>
                <tr id={detailId} hidden={!open}>
                  <S.DetailCell colSpan={width}>
                    {row.details.length > 0 && <S.Details>{detailsText(row.details)}</S.Details>}
                    {open && renderDetail?.(row.key)}
                  </S.DetailCell>
                </tr>
              </Fragment>
            )
          })}
        </tbody>
        <tfoot>
          <S.TotalRow>
            <S.RowHead scope="row" $sticky>Total dos comissários</S.RowHead>
            {data.total.cells.map((cell, index) => (
              <S.Cell key={data.columns[index].key} $numeric>
                <Amount cell={cell} />
              </S.Cell>
            ))}
            <S.Cell $numeric>
              <Amount cell={data.total.total} />
            </S.Cell>
            {data.showFee && <S.Cell $numeric>{formatCents(data.total.total.feeCents ?? 0)}</S.Cell>}
          </S.TotalRow>
        </tfoot>
      </S.Table>
    </S.Scroll>
  )
}

function MobileMatrix({ data, expandAll = false, renderDetail }: CommissionerMatrixProps): ReactElement {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const baseId = useId()
  const toggle = (key: string): void => {
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  return (
    <S.Cards>
      {data.rows.map((row) => {
        const open = expandAll || expanded.has(row.key)
        const detailId = `${baseId}-${row.key}`
        return (
          <S.Card key={row.key} aria-label={row.label}>
            <S.CardHeader>
              <S.CardTitle>{row.label}</S.CardTitle>
              <S.CardTotal>{`${count(row.total.quantity)} · ${formatCents(row.total.revenueCents)}`}</S.CardTotal>
            </S.CardHeader>
            <S.FormList>
              {row.cells.map((cell, index) => (
                <S.FormItem key={data.columns[index].key}>
                  <S.FormLabel>{data.columns[index].label}</S.FormLabel>
                  <S.FormValue>{cell === null ? '—' : `${count(cell.quantity)} · ${formatCents(cell.revenueCents)}`}</S.FormValue>
                </S.FormItem>
              ))}
            </S.FormList>
            {(row.details.length > 0 || renderDetail) && (
              <S.CardToggle type="button" aria-expanded={open} aria-controls={detailId} onClick={() => toggle(row.key)} data-print-hide="">
                {open ? 'Ocultar detalhes' : 'Ver detalhes'}
              </S.CardToggle>
            )}
            <div id={detailId} hidden={!open}>
              {open && row.details.length > 0 && <S.Details>{detailsText(row.details)}</S.Details>}
              {open && renderDetail?.(row.key)}
            </div>
          </S.Card>
        )
      })}
      <S.TotalCard aria-label="Total dos comissários">
        <S.CardHeader>
          <S.CardTitle>Total dos comissários</S.CardTitle>
          <S.CardTotal>{`${count(data.total.total.quantity)} · ${formatCents(data.total.total.revenueCents)}`}</S.CardTotal>
        </S.CardHeader>
      </S.TotalCard>
    </S.Cards>
  )
}

export function CommissionerMatrix(props: CommissionerMatrixProps): ReactElement {
  return (
    <S.Wrapper aria-label="Vendas por comissário e forma de pagamento">
      <S.Title>Por comissário</S.Title>
      {props.mobile === true ? <MobileMatrix {...props} /> : <DesktopMatrix {...props} />}
    </S.Wrapper>
  )
}
